import React from "react";
import PropTypes from "prop-types";
import { Typography, Box, TextField, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { COLORS } from "../constants";
import { Btn, SectionHeading } from "../components/UI";
import { LocationTree } from "../components/LocationTree";
import { ConfigCard } from "../components/ConfigCard";
import { useStyles } from "./browse-view.style";

const LOCATION_ICONS = ["🏛️", "🏢", "📍"];

export function BrowseView({
  search,
  setSearch,
  selectedLocation,
  setSelectedLocation,
  expandedNodes,
  toggleExpanded,
  config,
  locationNode,
  locationChain,
  openTemplateFromLocation,
  wizardReset,
  setTab,
  editId,
  editType,
  editData,
  setEditData,
  deleteConfirmation,
  setDeleteConfirmation,
  startEdit,
  cancelEdit,
  saveEdit,
  doDelete,
  locations,
  configLoading,
  configError,
  servicesRef,
  resourcesRef,
}) {
  const classes = useStyles();

  const onSelectLocation = (id) => {
    setSelectedLocation(id);
    cancelEdit();
    setDeleteConfirmation(null);
  };

  const onGoToInheritedLocation = (fromLoc) => {
    setSelectedLocation(fromLoc);
    cancelEdit();
    setTab("browse");
  };

  const servicesList = config?.services ?? [];
  const resourcesList = config?.resources ?? [];

  return (
    <Box className={classes.root}>
      <Box className={classes.sidebar}>
        <Box className={classes.sidebarHeader}>
          <Typography className={classes.sidebarTitle}>
            Location Hierarchy
          </Typography>
          <TextField
            placeholder="Search locations..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className={classes.treeContainer}>
          <LocationTree
            locations={locations}
            search={search}
            selected={selectedLocation}
            onSelect={onSelectLocation}
            mode="browse"
            expanded={expandedNodes}
            onToggle={toggleExpanded}
          />
        </Box>
      </Box>
      <Box className={classes.mainContent}>
        <Box className={classes.breadcrumbs}>
          {locationChain.map((node, index) => (
            <Typography component="span" key={node.id} variant="caption">
              {index > 0 && <Typography component="span" variant="caption" sx={{ margin: "0 3px" }}>›</Typography>}
              <button
                type="button"
                onClick={() => setSelectedLocation(node.id)}
                className={`${classes.breadcrumbButton} ${index === locationChain.length - 1 ? classes.breadcrumbActive : classes.breadcrumbInactive}`}
              >
                {node.name}
              </button>
            </Typography>
          ))}
        </Box>
        {(configLoading || configError) && (
          <Typography
            className={classes.statusMessage}
            sx={{ color: configError ? COLORS.error : COLORS.muted }}
          >
            {configLoading && "Loading config…"}
            {!configLoading && configError && `Error: ${configError}`}
          </Typography>
        )}
        <Box className={classes.locationHeader}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.body }}>
            {LOCATION_ICONS[locationNode?.level || 0]} {locationNode?.name}
          </Typography>
          <Box className={classes.locationActions}>
            <Btn variant="outlined" size="small" onClick={() => openTemplateFromLocation(selectedLocation)}>
              Template from Location
            </Btn>
            <Btn
              size="small"
              onClick={() => {
                setTab("wizard");
                wizardReset();
              }}
            >
              Open Wizard
            </Btn>
          </Box>
        </Box>
        <SectionHeading>Services ({servicesList.length})</SectionHeading>
        {servicesList.length === 0 && (
          <Typography className={classes.emptyState}>
            No services at this location.
          </Typography>
        )}
        {servicesList.map((service) => (
          <ConfigCard
            key={`svc-${service.id}`}
            item={service}
            type="svc"
            isEditing={editId === service.id && editType === "svc"}
            editData={editData}
            deleteConfirmation={deleteConfirmation}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onEditDataChange={setEditData}
            onSaveEdit={saveEdit}
            onDeleteClick={(id) => setDeleteConfirmation({ type: "svc", id })}
            onDeleteConfirm={doDelete}
            onClearDeleteConfirm={() => setDeleteConfirmation(null)}
            onGoToInheritedLocation={onGoToInheritedLocation}
            servicesRef={servicesRef}
            resourcesRef={resourcesRef}
          />
        ))}
        <SectionHeading>Resources ({resourcesList.length})</SectionHeading>
        {resourcesList.length === 0 && (
          <Typography className={classes.emptyState}>
            No resources at this location.
          </Typography>
        )}
        {resourcesList.map((resource) => (
          <ConfigCard
            key={`res-${resource.id}`}
            item={resource}
            type="res"
            isEditing={editId === resource.id && editType === "res"}
            editData={editData}
            deleteConfirmation={deleteConfirmation}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onEditDataChange={setEditData}
            onSaveEdit={saveEdit}
            onDeleteClick={(id) => setDeleteConfirmation({ type: "res", id })}
            onDeleteConfirm={doDelete}
            onClearDeleteConfirm={() => setDeleteConfirmation(null)}
            onGoToInheritedLocation={onGoToInheritedLocation}
            servicesRef={servicesRef}
            resourcesRef={resourcesRef}
          />
        ))}
      </Box>
    </Box>
  );
}

BrowseView.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func.isRequired,
  selectedLocation: PropTypes.string,
  setSelectedLocation: PropTypes.func.isRequired,
  expandedNodes: PropTypes.instanceOf(Set),
  toggleExpanded: PropTypes.func.isRequired,
  config: PropTypes.shape({ services: PropTypes.array, resources: PropTypes.array }),
  locationNode: PropTypes.shape({ level: PropTypes.number, name: PropTypes.string }),
  locationChain: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  openTemplateFromLocation: PropTypes.func.isRequired,
  wizardReset: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
  editId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editType: PropTypes.string,
  editData: PropTypes.object,
  setEditData: PropTypes.func.isRequired,
  deleteConfirmation: PropTypes.object,
  setDeleteConfirmation: PropTypes.func.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  saveEdit: PropTypes.func.isRequired,
  doDelete: PropTypes.func.isRequired,
  locations: PropTypes.array,
  configLoading: PropTypes.bool,
  configError: PropTypes.string,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

BrowseView.defaultProps = {
  search: "",
  selectedLocation: undefined,
  expandedNodes: undefined,
  config: undefined,
  locationNode: undefined,
  locationChain: [],
  editId: undefined,
  editType: undefined,
  editData: null,
  deleteConfirmation: null,
  locations: [],
  configLoading: false,
  configError: null,
  servicesRef: [],
  resourcesRef: [],
};
